import React, { useState, useEffect } from 'react';
import { Minus, Plus, AlertTriangle, CalendarCheck } from 'lucide-react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import { isRoughWeather } from './WeatherBadge';
import { generateWorkdays } from '../../utils/work-day-calculator';
import { isoDateString, justDate } from '../../utils/date-utils';
import { buildScheduleUpdate, markJobOrderScheduled } from '../../utils/schedule-job';

const WALNUT = '#8B4513';
const MOSS   = '#3B6B44';
const RUST   = '#A13D2D';
const STONE  = '#8C8275';

const QUICK_DAYS = [1, 2, 3, 4, 5];
const MAX_DAYS = 30;

// Shown when a job is dropped on a day: asks how many work days it takes,
// previews exactly which days that covers, then schedules it.
export default function QuickScheduleDialog({
  isOpen, onClose, job, date, forecastByDate = {}, updateJob, onScheduled, onMoreOptions,
}) {
  const [days, setDays] = useState(1);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setDays(Math.min(MAX_DAYS, Math.max(1, job?.durationDays || 1)));
      setError('');
    }
  }, [isOpen, job]);

  if (!job || !date) return null;

  const workDays = generateWorkdays(date, days);
  const startShifted = isoDateString(workDays[0]) !== isoDateString(justDate(date));
  const roughDays = workDays.filter(d => isRoughWeather(forecastByDate[isoDateString(d)]));
  const fmt = d => d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

  async function handleSchedule() {
    setSaving(true);
    setError('');
    try {
      await updateJob(job.id, buildScheduleUpdate(job, workDays[0], days));
      markJobOrderScheduled(job.id);
      onScheduled(job, workDays);
    } catch (err) {
      console.error('❌ Error scheduling job:', err);
      setError('Failed to schedule job. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  const chip = active => ({
    minWidth: '40px', padding: '8px 0', borderRadius: '8px', cursor: 'pointer',
    fontSize: '14px', fontWeight: '700', fontFamily: 'inherit',
    background: active ? WALNUT : 'transparent',
    color: active ? '#fff' : WALNUT,
    border: `1px solid ${active ? WALNUT : `${WALNUT}55`}`,
  });
  const stepBtn = {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    width: '32px', height: '32px', borderRadius: '8px', cursor: 'pointer',
    background: 'transparent', border: `1px solid ${STONE}66`, color: 'var(--text-primary)',
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="How long will it take?" maxWidth="480px">
      <div style={{ marginTop: '-14px', marginBottom: '18px', fontSize: '14px', color: 'var(--text-secondary)' }}>
        {job.contractNumber && <strong style={{ color: WALNUT }}>#{job.contractNumber} </strong>}
        <strong style={{ color: 'var(--text-primary)' }}>{job.customerName || job.name}</strong>
        {job.windowCount > 0 && ` · ${job.windowCount} window${job.windowCount === 1 ? '' : 's'}`}
        {job.doorCount > 0 && ` · ${job.doorCount} door${job.doorCount === 1 ? '' : 's'}`}
      </div>

      <div style={{ fontSize: '12px', fontWeight: '700', color: STONE, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '8px' }}>
        Work days
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
        {QUICK_DAYS.map(n => (
          <button key={n} type="button" style={chip(days === n)} onClick={() => setDays(n)}>{n}</button>
        ))}
        <span style={{ width: '10px' }} />
        <button type="button" style={stepBtn} onClick={() => setDays(d => Math.max(1, d - 1))} aria-label="One day less"><Minus size={14} /></button>
        <input
          type="number" min={1} max={MAX_DAYS} value={days}
          onChange={e => setDays(Math.min(MAX_DAYS, Math.max(1, parseInt(e.target.value, 10) || 1)))}
          style={{ width: '54px', padding: '6px', textAlign: 'center', fontSize: '14px', fontWeight: '700', border: `1px solid ${STONE}66`, borderRadius: '8px', fontFamily: 'inherit' }}
        />
        <button type="button" style={stepBtn} onClick={() => setDays(d => Math.min(MAX_DAYS, d + 1))} aria-label="One day more"><Plus size={14} /></button>
      </div>

      {/* Exactly which days this covers */}
      <div style={{ marginTop: '18px', padding: '12px 14px', background: `${MOSS}12`, borderLeft: `3px solid ${MOSS}`, borderRadius: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: '700', color: MOSS }}>
          <CalendarCheck size={14} strokeWidth={2} />
          {days === 1 ? fmt(workDays[0]) : `${fmt(workDays[0])} → ${fmt(workDays[workDays.length - 1])}`}
        </div>
        {days > 1 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '8px' }}>
            {workDays.map(d => (
              <span key={d.getTime()} style={{ fontSize: '11px', padding: '2px 7px', borderRadius: '999px', background: 'var(--bg-card)', color: 'var(--text-secondary)', border: `1px solid ${MOSS}33` }}>
                {fmt(d)}
              </span>
            ))}
          </div>
        )}
        <div style={{ fontSize: '11px', color: STONE, marginTop: '8px' }}>
          {startShifted
            ? `${fmt(justDate(date))} isn't a work day, so it starts ${fmt(workDays[0])}. `
            : ''}
          Weekends and holidays are skipped.
        </div>
      </div>

      {roughDays.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '10px', padding: '8px 12px', background: `${RUST}12`, borderRadius: '8px', fontSize: '12px', color: RUST, fontWeight: '600' }}>
          <AlertTriangle size={14} strokeWidth={2} style={{ flexShrink: 0 }} />
          Rough weather forecast on {roughDays.map(fmt).join(', ')}.
        </div>
      )}

      {error && <div style={{ marginTop: '10px', fontSize: '12px', color: RUST }}>{error}</div>}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', marginTop: '22px' }}>
        <button
          type="button" onClick={onMoreOptions}
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: '12px', color: STONE, textDecoration: 'underline', fontFamily: 'inherit' }}
        >
          More options (crew)…
        </button>
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button variant="secondary" size="small" onClick={onClose}>Cancel</Button>
          <Button size="small" onClick={handleSchedule} disabled={saving}>
            {saving ? 'Scheduling…' : `Schedule ${days} day${days === 1 ? '' : 's'}`}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
